// ============================================================================
// AXIOM — NumberTheory
// ----------------------------------------------------------------------------
// The primitives the *gameplay* is built out of: Prime Crit (§4.1), the
// Fibonacci Chain (§4.2), and the constants that ride in as operator tokens.
// Everything here must be cheap enough to call once per bullet impact.
// ============================================================================

import BigNum from './BigNum.js';

export const PHI = 1.618033988749895;
export const E = Math.E;
export const PI = Math.PI;
export const SQRT5 = Math.sqrt(5);
export const LOG10_PHI = Math.log10(PHI);

/** Deterministic Miller-Rabin witness set — provably correct for all n < 3.317e24. */
const MR_BASES = [2n, 3n, 5n, 7n, 11n, 13n, 17n, 19n, 23n, 29n, 31n, 37n];
const SMALL_PRIMES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47];

// Bit-packed sieve over [0, SIEVE_LIMIT). 1M bits = 128 KB, built once, lazily.
const SIEVE_LIMIT = 1 << 20;
let sieve = null;

function buildSieve() {
  const bits = new Uint8Array(SIEVE_LIMIT >> 3);
  const set = (i) => { bits[i >> 3] |= 1 << (i & 7); };
  set(0); set(1);
  for (let i = 2; i * i < SIEVE_LIMIT; i++) {
    if (bits[i >> 3] & (1 << (i & 7))) continue;
    for (let j = i * i; j < SIEVE_LIMIT; j += i) set(j);
  }
  sieve = bits;
  return bits;
}

function sieveIsPrime(n) {
  const s = sieve || buildSieve();
  return (s[n >> 3] & (1 << (n & 7))) === 0;
}

// Built eagerly at module load. Lazily it landed inside the FIRST evaluate() of
// every fresh run — early-game damage sits below 2^20, so every player paid a
// ~5ms sieve construction as a dropped frame on their first shot. At import
// time the same 5ms disappears into the loading screen.
buildSieve();

function powMod(base, exp, mod) {
  let r = 1n;
  base %= mod;
  while (exp > 0n) {
    if (exp & 1n) r = (r * base) % mod;
    base = (base * base) % mod;
    exp >>= 1n;
  }
  return r;
}

/**
 * Deterministic Miller-Rabin. Exact for every integer a double can hold.
 * @param {number} n
 * @returns {boolean}
 */
export function isPrime(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return false;
  if (n < 2) return false;
  n = Math.floor(n);
  if (n > Number.MAX_SAFE_INTEGER) return false;    // cannot be trusted as an integer

  for (let i = 0; i < SMALL_PRIMES.length; i++) {
    const p = SMALL_PRIMES[i];
    if (n === p) return true;
    if (n % p === 0) return false;
  }
  if (n < SIEVE_LIMIT) return sieveIsPrime(n);

  const N = BigInt(n);
  let d = N - 1n;
  let r = 0n;
  while ((d & 1n) === 0n) { d >>= 1n; r++; }

  outer:
  for (let i = 0; i < MR_BASES.length; i++) {
    const a = MR_BASES[i] % N;
    if (a === 0n) continue;
    let x = powMod(a, d, N);
    if (x === 1n || x === N - 1n) continue;
    for (let k = 1n; k < r; k++) {
      x = (x * x) % N;
      if (x === N - 1n) continue outer;
    }
    return false;
  }
  return true;
}

/**
 * Prime test on a BigNum. Per the contract: tests the leading integer form and
 * returns false once the value exceeds 2^53 (where "the integer part" stops
 * being a well-defined thing a double can name).
 * @param {BigNum} bn
 */
export function isPrimeBig(bn) {
  if (!bn || typeof bn.logAbs10 !== 'function') return false;
  if (bn.isNaN() || bn.isNeg() || bn.isZero()) return false;
  if (bn.e > 15) return false;                      // > ~1e16 > 2^53
  const v = bn.toNumber();
  if (!Number.isFinite(v) || v > Number.MAX_SAFE_INTEGER) return false;
  return isPrime(Math.floor(v));
}

/**
 * EXTENSION (not in ARCHITECTURE.md): prime test on the leading `digits` digits
 * of a huge BigNum. Without this the Prime Crit mechanic silently dies the
 * moment damage crosses 1e16, which is around wave 40 — i.e. it would be dead
 * for most of the run. Formula.evaluate() falls back to this above 2^53 so the
 * crit rate stays ~1/ln(10^15) ≈ 2.9% forever instead of dropping to zero.
 * @param {BigNum} bn
 * @param {number} digits  how many leading digits to interpret as an integer
 */
export function isPrimeLeading(bn, digits = 15) {
  if (!bn || typeof bn.logAbs10 !== 'function') return false;
  if (bn.isNaN() || bn.isNeg() || bn.isZero()) return false;
  if (bn.e <= 15) return isPrimeBig(bn);
  const d = Math.max(2, Math.min(15, digits | 0));
  const lead = Math.floor(Math.abs(bn.m) * Math.pow(10, d - 1));
  return isPrime(lead);
}

/** Next prime >= n (bounded search; returns 0 if nothing found inside the window). */
export function nextPrime(n, budget = 4096) {
  let k = Math.max(2, Math.ceil(n));
  for (let i = 0; i < budget; i++, k++) if (isPrime(k)) return k;
  return 0;
}

/** Largest prime <= n (bounded search; returns 0 if nothing found). */
export function prevPrime(n, budget = 4096) {
  let k = Math.floor(n);
  for (let i = 0; i < budget && k >= 2; i++, k--) if (isPrime(k)) return k;
  return 0;
}

/**
 * Prime nearest to n, ties going down. Used by the `mod` + Sieve-of-Eratosthenes
 * perk — which is why `budget` exists. This runs on the SHOT PATH, on 15-digit
 * inputs, where a wide prime gap means hundreds of Miller-Rabin rounds: a real
 * gap of width 1,132 after 1693182318746371 costs ~2ms, ten times the frame
 * budget for a shot. Callers on the hot path pass a small budget and accept a
 * miss — a snap that occasionally fails is invisible; a 2ms shot is not.
 */
export function nearestPrime(n, budget = 4096) {
  if (n < 2) return 2;
  const lo = prevPrime(n, budget);
  const hi = nextPrime(n, budget);
  if (!lo) return hi;
  if (!hi) return lo;
  return (n - lo) <= (hi - n) ? lo : hi;
}

// Every even perfect number below 2^53 (Euclid-Euler: 2^(p-1)(2^p - 1)).
const PERFECTS = [6, 28, 496, 8128, 33550336, 8589869056, 137438691328];

/**
 * Perfect-number test. Uses the Euclid-Euler list below 2^53 (no odd perfect
 * number is known, and none exists below 10^1500), with an honest divisor sum
 * as a fallback for small inputs.
 */
export function isPerfect(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return false;
  n = Math.floor(n);
  if (n < 2) return false;
  if (n <= Number.MAX_SAFE_INTEGER && PERFECTS.includes(n)) return true;
  if (n > 1e7) return false;                        // divisor sum is too slow past this
  let sum = 1;
  const r = Math.floor(Math.sqrt(n));
  for (let i = 2; i <= r; i++) {
    if (n % i === 0) {
      sum += i;
      const j = n / i;
      if (j !== i) sum += j;
    }
  }
  return sum === n;
}

// ------------------------------------------------------------------ Fibonacci

const FIB_CACHE = [0, 1];
const FIB_MAX_N = 1476;                             // F(1476) is the last finite double

/** Memoized Fibonacci. Exact to n=78, float beyond; saturates instead of returning Infinity. */
export function fib(n) {
  n = Math.floor(Number(n));
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > FIB_MAX_N) return Number.MAX_VALUE;
  for (let i = FIB_CACHE.length; i <= n; i++) {
    FIB_CACHE[i] = FIB_CACHE[i - 1] + FIB_CACHE[i - 2];
  }
  return FIB_CACHE[n];
}

/**
 * EXTENSION: Fibonacci as a BigNum, via Binet in log space. The chain can run
 * past n=1476 in a good run and the multiplier still has to be a real number.
 * @returns {BigNum}
 */
export function fibBig(n) {
  n = Math.floor(Number(n));
  if (!Number.isFinite(n) || n < 0) return BigNum.ZERO;
  if (n <= 78) return BigNum.from(fib(n));
  return BigNum.fromLog10(n * LOG10_PHI - Math.log10(SQRT5));
}

/** phi^n as a BigNum — the chain multiplier the HUD renders as `φ^chain`. */
export function phiPow(n) {
  const k = Number(n);
  if (!Number.isFinite(k) || k <= 0) return BigNum.ONE;
  return BigNum.fromLog10(k * LOG10_PHI);
}

// ------------------------------------------------------------------- divisors

export function gcd(a, b) {
  a = Math.abs(Math.floor(Number(a) || 0));
  b = Math.abs(Math.floor(Number(b) || 0));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  while (b) { const t = a % b; a = b; b = t; }
  return a;
}

export function lcm(a, b) {
  const g = gcd(a, b);
  return g === 0 ? 0 : Math.abs(a * b) / g;
}

/** Number of decimal digits in the integer part — the `!` token's operand. */
export function digitCount(bn) {
  const L = bn && typeof bn.logAbs10 === 'function' ? bn.logAbs10() : Math.log10(Math.abs(Number(bn) || 1));
  if (!Number.isFinite(L)) return 1;
  return Math.max(1, Math.floor(L) + 1);
}

export default {
  isPrime, isPrimeBig, isPrimeLeading, isPerfect, fib, fibBig, phiPow,
  gcd, lcm, nextPrime, prevPrime, nearestPrime, digitCount,
  PHI, E, PI,
};
